import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { map } from 'rxjs';
import { Project, ProjectsState, ProjectsStore } from '../project';
import { JobStatus } from '../../../../../../api/src/encode/encode-job.entity';

@Injectable({ providedIn: 'root' })
export class EncodeJobQuery extends QueryEntity<ProjectsState> {
	selectPreviews(projectId: Project['id']) {
		return this.selectEntity(projectId).pipe(
			map(project => {
				return project?.encodeJobs?.filter(encodeJob => encodeJob?.tag === 'preview');
			})
		);
	}

	selectLatestPreview(projectId: Project['id']) {
		return this.selectPreviews(projectId).pipe(
			map(previews => {
				if (!previews?.length) return undefined;
				return previews.reduce((prev, current) => {
					return prev?.created > current?.created ? prev : current;
				});
			})
		);
	}

	selectFinal(projectId: Project['id']) {
		return this.selectEntity(projectId).pipe(
			map(project => {
				return project?.encodeJobs?.find(encodeJob => encodeJob?.tag === 'final');
			})
		);
	}

	selectExports(projectId: Project['id']) {
		return this.selectEntity(projectId).pipe(
			map(project => {
				return project.encodeJobs?.filter(encodeJob => encodeJob?.tag === 'export');
			})
		);
	}

	selectLatestExport(projectId: Project['id']) {
		return this.selectExports(projectId).pipe(
			map(exports => {
				return exports?.reduce((prev, current) => {
					return prev?.created > current?.created ? prev : current;
				}, null);
			})
		);
	}

	selectExportHistory(projectId: Project['id']) {
		return this.selectEntity(projectId).pipe(
			map(project =>
				project.encodeJobs?.filter(encodeJob => {
					return encodeJob?.tag === 'export' && encodeJob.status === JobStatus.Completed;
				})
			)
		);
	}

	constructor(protected override store: ProjectsStore) {
		super(store);
	}

	getPreviews(projectId: Project['id']) {
		return this.getValue()?.entities[projectId]?.encodeJobs?.filter(encodeJob => encodeJob?.tag === 'preview');
	}

	getLatestPreview(projectId: Project['id']) {
		const previews = this.getPreviews(projectId);
		if (!previews?.length) return undefined;
		return previews.reduce((prev, current) => {
			return prev?.created > current?.created ? prev : current;
		});
	}

	getExports(projectId: Project['id']) {
		return this.getValue()?.entities[projectId]?.encodeJobs?.filter(encodeJob => encodeJob?.tag === 'export');
	}

	getLatestExport(projectId: Project['id']) {
		const exports = this.getExports(projectId);
		if (!exports?.length) return undefined;
		return exports.reduce((prev, current) => {
			return prev?.created > current?.created ? prev : current;
		});
	}

	getFinal(projectId: Project['id']) {
		return this.getValue()?.entities[projectId]?.encodeJobs?.find(encodeJob => encodeJob?.tag === 'final');
	}
}
